import { useState, useEffect, useRef } from "react";
import { Link } from "@/components/common/Link";
import { Sparkle } from "lucide-react";
export default function HeroBanner() {
  const [activeLink, setActiveLink] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  const navLinks = [{
    name: "Sunroof Film",
    path: "#sunroof"
  }, {
    name: "Windshield Film",
    path: "#windshield"
  }, {
    name: "Car Headlight Film",
    path: "#headlight"
  }];
  return <section ref={sectionRef} className="dark relative w-full overflow-hidden bg-background" style={{
    minHeight: "100vh"
  }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Inter:wght@300;400;500&family=Bebas+Neue&display=swap');`}</style>

      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696675/img/IMG-20260604-WA00021.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696675/img/IMG-20260604-WA00021.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696675/img/IMG-20260604-WA00021.jpg-wvc-sizes" className="w-full h-auto" style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "center"
      }} alt="Advanced Automotive Protection Film" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />
      </div>

      <div className="absolute inset-y-0 left-0 w-px z-10 ml-16 md:ml-24 flex flex-col items-center justify-center gap-6">
        <div className={`w-px bg-white/20 transition-all duration-1000 delay-300 ${visible ? "h-[35%] opacity-100" : "h-0 opacity-0"}`} />
        <div className={`w-2.5 h-2.5 rounded-lg bg-secondary shadow-[0_0_10px_rgba(165,62,58,0.5)] transition-all duration-700 delay-500 ${visible ? "scale-100 opacity-100" : "scale-0 opacity-0"}`} />
        <div className={`w-px bg-white/10 transition-all duration-1000 delay-700 ${visible ? "h-[35%] opacity-100" : "h-0 opacity-0"}`} />
      </div>

      <div className="relative z-10 flex flex-col justify-between min-h-screen px-8 md:px-24 lg:px-32 py-16 md:py-24">
        <div className="flex items-start justify-between">
          <div className="text-xs tracking-[0.4em] text-white/40 uppercase" style={{
          fontFamily: "'Inter', sans-serif",
          fontWeight: 300
        }}>
            Matrix PPF
          </div>
          <div className="hidden md:flex gap-8 text-xs tracking-widest text-white/30 uppercase" style={{
          fontFamily: "'Inter', sans-serif",
          fontWeight: 300
        }}>
            <span>01</span>
            <span className="text-white/60">— Protection</span>
          </div>
        </div>

        <div className="flex flex-col gap-6 max-w-4xl">
          <div className={`transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: "200ms"
        }}>
            <div className="text-xs tracking-[0.5em] text-white/50 uppercase mb-6 flex items-center gap-4" style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400
          }}>
              <span className="w-8 h-px bg-secondary" />
              Automotive Protection
            </div>
            <div className="relative inline-flex flex-col items-start">
              <h1 className="text-6xl sm:text-7xl md:text-[9rem] lg:text-[11rem] font-black leading-[0.8] tracking-tighter uppercase bg-linear-to-b from-slate-100 via-gray-100 to-slate-600 bg-clip-text text-transparent" style={{
              fontFamily: "'Orbitron', sans-serif",
              filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.8))",
              textShadow: "1px 1px 0px #fff, -1px -1px 0px #666, 2px 2px 2px rgba(0,0,0,0.5)"
            }}>
                Matri
                <span className="relative inline-block">
                  X
                </span>
              </h1>
              <div className="text-3xl sm:text-4xl md:text-[4.5rem] lg:text-[5.5rem] font-black leading-none tracking-[0.2em] uppercase bg-linear-to-b from-slate-100 via-gray-100 to-slate-600 bg-clip-text text-transparent self-center md:self-end md:mr-[10%]" style={{
              fontFamily: "'Orbitron', sans-serif",
              filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.8))",
              textShadow: "1px 1px 0px #fff, -1px -1px 0px #666, 2px 2px 2px rgba(0,0,0,0.5)"
            }}>
                <span>P</span>PF
              </div>
              <div className={`absolute -right-4 md:-right-8 top-1/2 -translate-y-1/2 w-2 md:w-3 bg-secondary transition-all duration-1000 delay-500 ${visible ? "h-full opacity-100" : "h-0 opacity-0"}`} style={{
              backgroundColor: "hsl(165 62% 58%)"
            }} />
            </div>
          </div>
        </div>

        <div className={`flex flex-col md:flex-row items-start md:items-end justify-between gap-8 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        transitionDelay: "600ms"
      }}>
          <div className="flex flex-col gap-4">
            <div className="text-xs tracking-[0.4em] text-white/30 uppercase mb-2" style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300
          }}>
              Product Range
            </div>
            <nav className="flex flex-col md:flex-row gap-6 md:gap-10">
              {navLinks.map((link, i) => <Link key={link.name} to={link.path} data-index={i} onMouseEnter={() => setActiveLink(link.name)} onMouseLeave={() => setActiveLink(null)} className={`group relative flex items-center gap-4 transition-all duration-500 ${activeLink === link.name ? "text-primary" : "text-white/50"}`} style={{
              fontFamily: "'Inter', sans-serif"
            }}>
                  <span className={`text-[0.65rem] tracking-widest transition-all duration-500 ${activeLink === link.name ? "text-primary/80" : "text-white/20"}`} style={{
                fontFamily: "'Bebas Neue', sans-serif"
              }}>
                    0{i + 1}
                  </span>
                  <span className={`text-sm tracking-[0.2em] uppercase transition-all duration-500 ${activeLink === link.name ? "drop-shadow-[0_0_8px_rgba(var(--primary),0.5)]" : ""}`} style={{
                fontWeight: 500
              }}>
                    {link.name}
                  </span>
                  <div className={`absolute -bottom-2 left-8 h-0.5 bg-primary transition-all duration-500 ${activeLink === link.name ? "w-[calc(100%-2rem)] opacity-100" : "w-0 opacity-0"}`} />
                </Link>)}
            </nav>
          </div>

          <div className="hidden md:flex items-center gap-4 group cursor-pointer">
            <div className="w-12 h-px bg-white/20 group-hover:w-16 group-hover:bg-secondary transition-all duration-500" />
            <span className="text-xs tracking-[0.3em] uppercase text-white/40 group-hover:text-secondary transition-colors duration-500" style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400
          }}>
              Scroll
            </span>
            <div className="relative flex items-center justify-center">
              <div className="absolute w-4 h-4 rounded-lg bg-secondary/20 animate-ping" />
              <div className="w-2 h-2 rounded-lg bg-secondary" />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute right-8 md:right-12 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-4">
        {[0, 1, 2].map(i => <button key={i} data-index={i} className="group relative transition-all duration-500 outline-none flex items-center justify-center">
            {i === 0 ? <Sparkle className="w-5 h-5 text-gray-200 scale-110 drop-shadow-md" /> : <Sparkle className="w-4 h-4 text-gray-400 opacity-50 scale-75" />}
            <div className="absolute inset-0 rounded-lg bg-secondary opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-500 blur-sm" />
          </button>)}
      </div>
    </section>;
}